import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, formatApiError } from "../contexts/AuthContext";
import { useLang } from "../contexts/LanguageContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Eye, EyeSlash, SpinnerGap, Clock, XCircle, Stethoscope, DeviceMobile } from "@phosphor-icons/react";
import { KNORIA_LOGO_URL } from "../components/KnoriaLogo";
import ConnectionDiagnostic from "../components/ConnectionDiagnostic";
import { PublicLangToggle } from "../components/PublicLangToggle";

export default function LoginPage() {
  const { lang, t } = useLang();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [showDiagnostic, setShowDiagnostic] = useState(false);
  const [pendingState, setPendingState] = useState(null);
  const [loading, setLoading] = useState(false);
  // Vincent #208 (Eric 2026-05-28) — TOTP step
  const [needTotp, setNeedTotp] = useState(false);
  const [totpCode, setTotpCode] = useState("");
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setShowDiagnostic(false);
    setPendingState(null);
    setLoading(true);
    try {
      await login(email, password, needTotp ? totpCode.trim() : null);
      navigate("/dashboard");
    } catch (err) {
      if (err.message === "PENDING_APPROVAL") {
        setPendingState("pending");
      } else if (err.message === "REJECTED") {
        setPendingState("rejected");
      } else if (err.message === "TOTP_REQUIRED") {
        // Move to step 2 — show TOTP input
        setNeedTotp(true);
        setError("");
      } else if (err._isCanceled || err._navigationAbort) {
        // B1048 (Eric 2026-09-03 SPEC STRICK PROD) — Le POST /api/auth/login
        // a été aborté par un reload de navigation (emergent-main.js « api
        // version mismatch »). Le backend est joignable, la requête a juste
        // été coupée en vol. Message clair : réessaie, pas de « Vérifiez
        // votre internet » trompeur, pas de diagnostic (endpoints publics OK).
        setError(lang === "en"
          ? "Login request was interrupted (the page reloaded). Please click Sign In again."
          : "Requête de connexion interrompue (la page s'est rechargée). Clique à nouveau sur « Se connecter ».");
      } else if (!err.response && err.message) {
        // B1044 (Eric 2026-09-02) — Si le circuit-breaker global s'est
        // déclenché (backend intermittent), on aligne le message avec le
        // banner du haut au lieu du trompeur « Vérifiez votre internet ».
        // Signal explicite marqué par l'interceptor AuthContext.
        if (err._breakerOpen) {
          setError(lang === "en"
            ? "Backend Knoria intermittently unreachable — no action needed, the app is retrying automatically. Try again in a few seconds."
            : "Backend Knoria intermittent — rien à faire, l'app ré-essaie toute seule. Réessaie dans quelques secondes.");
        } else {
          setError(lang === "en"
            ? "Connection error. Please check your internet and try again."
            : "Erreur de connexion. Vérifiez votre internet et réessayez.");
        }
        setShowDiagnostic(true);
      } else if (err.response?.status >= 500) {
        setError(lang === "en"
          ? `Server error (${err.response.status}). Please try again in a few seconds — your session is still valid.`
          : `Erreur serveur (${err.response.status}). Réessayez dans quelques secondes — votre session reste valide.`);
        setShowDiagnostic(true);
      } else {
        setError(formatApiError(err.response?.data?.detail) || err.message);
        // If TOTP was wrong, stay on TOTP step (don't go back to password)
      }
    } finally {
      setLoading(false);
    }
  };

  const cancelTotp = () => {
    setNeedTotp(false);
    setTotpCode("");
    setError("");
  };

  return (
    <div className="min-h-screen flex flex-col">
      <div style={{ backgroundColor: "#08123A" }} className="relative pt-10 pb-4 px-6 flex items-center justify-center">
        <img src={KNORIA_LOGO_URL} alt="KNORIA" className="w-48 sm:w-56 block" data-testid="login-logo" />
        <div className="absolute top-3 right-3">
          <PublicLangToggle />
        </div>
      </div>
      <div className="flex-1 bg-white flex flex-col items-center px-6 pt-10 pb-8">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <span className="overline text-zinc-400">{t("welcome_back")}</span>
            <h2 className="text-3xl font-semibold tracking-tight font-['Outfit'] mt-2 text-zinc-900">{t("sign_in")}</h2>
          </div>

          {pendingState === "pending" && (
            <div className="mb-6 p-5 bg-amber-50 border border-amber-300 rounded-xl text-center animate-fade-in" data-testid="pending-approval-banner">
              <Clock size={36} className="mx-auto mb-2 text-amber-500" />
              <h3 className="font-semibold text-amber-800 text-base mb-1">
                {lang === "en" ? "Account Pending Approval" : "Compte en attente d'approbation"}
              </h3>
              <p className="text-sm text-amber-700">
                {lang === "en"
                  ? "Your access request is being reviewed by an administrator. You will receive an email once approved."
                  : "Votre demande d'accès est en cours de révision par un administrateur. Vous recevrez un courriel une fois approuvée."}
              </p>
            </div>
          )}

          {pendingState === "rejected" && (
            <div className="mb-6 p-5 bg-red-50 border border-red-300 rounded-xl text-center animate-fade-in" data-testid="rejected-banner">
              <XCircle size={36} className="mx-auto mb-2 text-red-500" />
              <h3 className="font-semibold text-red-800 text-base mb-1">
                {lang === "en" ? "Access Denied" : "Accès refusé"}
              </h3>
              <p className="text-sm text-red-700">
                {lang === "en"
                  ? "Your access request has been declined. Contact the administrator for more information."
                  : "Votre demande d'accès a été refusée. Contactez l'administrateur pour plus d'informations."}
              </p>
            </div>
          )}

          {!pendingState && (
            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm animate-fade-in" data-testid="login-error">
                  <div>{error}</div>
                  {!showDiagnostic && (
                    <button
                      type="button"
                      onClick={() => setShowDiagnostic(true)}
                      className="mt-2 text-xs text-red-700 underline hover:text-red-900 inline-flex items-center gap-1"
                      data-testid="login-show-diagnostic-btn"
                    >
                      <Stethoscope size={12} weight="bold" />
                      {lang === "en" ? "Run connection diagnostic" : "Lancer le diagnostic de connexion"}
                    </button>
                  )}
                </div>
              )}
              {showDiagnostic && (
                <div className="bg-white border border-zinc-200 rounded-lg p-3" data-testid="login-diagnostic-wrap">
                  <ConnectionDiagnostic userEmail={email} />
                </div>
              )}
              {/* Vincent #208 (Eric 2026-05-28) — Step 2 : code TOTP demandé */}
              {needTotp ? (
                <div className="space-y-3 animate-fade-in" data-testid="totp-step">
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-center">
                    <DeviceMobile size={32} className="mx-auto mb-2 text-[#002FA7]" />
                    <h3 className="font-semibold text-zinc-800 text-sm mb-1">
                      {lang === "en" ? "Verification code" : "Code de vérification"}
                    </h3>
                    <p className="text-xs text-zinc-600">
                      {lang === "en"
                        ? "Open your authenticator app and enter the 6-digit code (or a backup code)."
                        : "Ouvre ton application d'authentification et entre le code à 6 chiffres (ou un code de secours)."}
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="totp" className="text-zinc-700 font-medium">
                      {lang === "en" ? "TOTP code or backup code" : "Code TOTP ou code de secours"}
                    </Label>
                    <Input
                      id="totp"
                      type="text"
                      value={totpCode}
                      onChange={(e) => setTotpCode(e.target.value)}
                      placeholder="000000"
                      autoFocus
                      autoComplete="one-time-code"
                      inputMode="text"
                      className="h-14 rounded-lg text-center text-2xl font-mono tracking-widest"
                      data-testid="login-totp-input"
                    />
                  </div>
                  <Button type="submit" disabled={loading || !totpCode.trim()} className="w-full h-12 rounded-lg bg-[#002FA7] hover:bg-[#002280] text-white font-semibold text-base" data-testid="login-totp-submit">
                    {loading ? <SpinnerGap className="animate-spin" size={20} /> : (lang === "en" ? "Verify and log in" : "Vérifier et se connecter")}
                  </Button>
                  <button type="button" onClick={cancelTotp} className="w-full text-center text-xs text-zinc-500 hover:text-zinc-700" data-testid="login-totp-cancel">
                    {lang === "en" ? "← Back to password" : "← Retour au mot de passe"}
                  </button>
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-zinc-700 font-medium">{t("email_address")}</Label>
                    <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("email_placeholder")} required className="h-12 rounded-lg" data-testid="login-email-input" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-zinc-700 font-medium">{t("password")}</Label>
                    <div className="relative">
                      <Input id="password" type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required className="h-12 rounded-lg pr-10" data-testid="login-password-input" />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600" data-testid="toggle-password-visibility">
                        {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                      </button>
                    </div>
                  </div>
                  <Button type="submit" disabled={loading} className="w-full h-12 rounded-lg bg-[#002FA7] hover:bg-[#002280] text-white font-semibold text-base" data-testid="login-submit-btn">
                    {loading ? <SpinnerGap className="animate-spin" size={20} /> : t("login_btn")}
                  </Button>
                  {/* Vincent #191 (Eric 2026-05-28) — Bouton "Mot de passe oublié"
                      Renvoie vers /forgot-password — l'utilisateur saisit son email,
                      l'admin de son org est notifié pour faire le reset manuel. */}
                  <Link to="/forgot-password" className="block text-center text-xs text-[#002FA7] hover:underline mt-2" data-testid="forgot-password-link">
                    {lang === "en" ? "Forgot password?" : "Mot de passe oublié ?"}
                  </Link>
                </>
              )}
            </form>
          )}

          {pendingState && (
            <Button variant="outline" onClick={() => setPendingState(null)} className="w-full mt-4" data-testid="try-again-btn">
              {lang === "en" ? "Try another account" : "Essayer un autre compte"}
            </Button>
          )}

          <p className="mt-6 text-center text-sm text-zinc-500">
            {t("no_account")}{" "}
            <Link to="/register" className="text-[#002FA7] hover:underline font-medium" data-testid="register-link">{t("request_access")}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
